import { Component, computed, effect, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { BuildingPickListItemDto, TeamDetailDto, TeamMergeDto, TeamService, UserItemDto } from '@artemis-software/wr-api';
import { createForm } from '@sonofabit/ng-core';
import { Validators } from '@angular/forms';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { tryWithLoading } from '@/utils/async-utils';
import { PendingChanges } from '@guards/pending-changes.guard';
import { currentTenantId } from '@/utils/admin-utils';
import { routeParam } from '@/utils/signals/router';
import { AssignmentType } from '@shared/form-controls/generic-pick-list/generic-pick-list.component';

@Component({
  standalone: false,
  selector: 'wr-team-detail-page',
  templateUrl: './team-detail-page.component.html',
  styleUrls: ['./team-detail-page.component.scss']
})
export class TeamDetailPageComponent implements PendingChanges {
  loading$ = new BehaviorSubject(false);
  isEditing = false;

  readonly teamId = routeParam('id', true);

  tenantFilter$ = new BehaviorSubject<string[]>([]);
  readonly currentTenantId = currentTenantId();

  firstSubmit = false;

  assignedUsers: WritableSignal<UserItemDto[]> = signal([]);
  team: WritableSignal<TeamDetailDto | undefined> = signal(undefined);

  form?: ReturnType<typeof this.createFormFromDetail>;

  readonly canEdit = signal(false);
  readonly isNew = computed(() => this.teamId() && this.teamId() === 'new');

  readonly assignmentTypeFunctionUser = (item: UserItemDto): AssignmentType => {
    if (item.teamIds.find(teamId => teamId === this.teamId())) {
      return AssignmentType.ASSIGNED;
    } else if (item.teamIds.length) {
      return AssignmentType.ASSIGNED_ELSEWHERE;
    }
    return AssignmentType.UNASSIGNED;
  };

  readonly assignmentTypeFunctionBuilding = (item: BuildingPickListItemDto): AssignmentType => {
    if (item.teamId == this.teamId()) {
      return AssignmentType.ASSIGNED;
    } else if (item.teamId !== this.teamId() && item.teamId) {
      return AssignmentType.ASSIGNED_ELSEWHERE;
    }
    return AssignmentType.UNASSIGNED;
  };

  constructor(private readonly router: Router,
    route: ActivatedRoute,
              private readonly dialogService: NbDialogService,
              private readonly teamService: TeamService,
              private readonly nbToastrService: NbToastrService,) {
    route.paramMap.subscribe(async (params) => {
      const id = this.teamId();
      if (id) {
        if (id === 'new') {
          this.isEditing = true;
          this.canEdit.set(true);
          this.form = this.createFormFromDetail(null);
        } else {
          this.teamService.findById(id).subscribe((team) => {
            this.canEdit.set(team.permissions.canEdit);
            this.form = this.createFormFromDetail(team);
            this.form?.disable();
            this.team.set(team);
          });
        }
      } else {
        await this.router.navigate(['teams']);
      }
    });
    effect(() => {
      const tenantId = this.currentTenantId();
      if (tenantId) {
        this.tenantFilter$.next([tenantId]);
      }
    });
    effect(() => {
      const team = this.team();
      if (team) {
        this.assignedUsers.set(team?.members);
      }
    });
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['teams']);
  }

  onEdit(): void {
    this.isEditing = true;
    this.form?.enable();
  }

  createFormFromDetail(detail: TeamDetailDto | null) {
    return createForm(({ field }) => {
      return {
        id: field({ value: detail?.id }),
        name: field({ value: detail?.name, validators: [Validators.required] }),
        abbreviation: field({ value: detail?.abbreviation, validators: [Validators.required] }),
        description: field({ value: detail?.description }),
        color: field({ value: detail?.color, validators: [Validators.required] }),
        members: field({ value: detail?.members }),
        leader: field({ value: detail?.leader, validators: [Validators.required] }),
        deputyManager: field({ value: (detail as any)?.deputyManager }),
        buildings: field({ value: detail?.buildings }),
      };
    });
  }

  async onDelete(): Promise<void> {
    if (await firstValueFrom(this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Team löschen',
        message: 'Bist du sicher, dass du das Team löschen willst?',
      },
    }).onClose) && this.form?.value.id) {
      await firstValueFrom(this.teamService.deleteById(this.form?.value.id));
      await this.router.navigate(['teams']);
      this.nbToastrService.success('Teams wurde erfolgreich gelöscht', 'Teams');
    }
  }

  async submit(): Promise<void> {
    this.firstSubmit = true;
    this.form?.markAsTouched();

    if (!this.form?.valid) {
      this.nbToastrService.danger('Team kann nicht gespeichert werden, es gibt noch Fehler.', 'Team');
      return;
    }

    await tryWithLoading(this.loading$, async () => {
      const teamDto = {
        ...this.form?.value,
        memberIds: this.form?.value.members?.map(member => member.id),
        buildingIds: this.form?.value.buildings?.map(building => building.id),
        leaderId: this.form?.value.leader?.id,
        deputyManagerId: this.form?.value.deputyManager?.id,
      } as TeamMergeDto;
      const saved = await firstValueFrom(this.teamService.merge(teamDto));
      this.form?.get('id')?.setValue(saved.id);
      await this.reloadForm();
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['team', saved.id]).then(() => {
          this.nbToastrService.success('Team wurde erfolgreich gespeichert', 'Team');
        });
      });
    });
  }

  async reloadForm(): Promise<void> {
    await tryWithLoading(this.loading$, async () => {
      const id = this.form?.value.id;
      if (id) {
        const team = await firstValueFrom(this.teamService.findById(id));
        this.form = this.createFormFromDetail(team);
      }
    });
    this.isEditing = false;
    this.form?.disable();
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return this.isEditing;
  }

  async cancel(): Promise<void> {
    if (!this.isNew()) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['team', this.teamId]);
      });
    } else {
      await this.router.navigate(['teams']);
    }
  }
}
